import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';

import { useDrawingManagerContext } from '../context/drawing-manager-context.js';
import { hoverInfoEnabledAtom } from '../hover-info/hover-info-atom.js';
import { ClearSelectionToolbarItem } from './clear-selection-toolbar-item.js';
import { LayersToolbarItem } from './layers-toolbar-item/layers-toolbar-item.js';
import { SelectionToolbarItem } from './selection-toolbar-item.js';
import { ToggleHoverInfoToolbarItem } from './toggle-hover-info-toolbar-item.js';
import { TogglePadLabelsToolbarItem } from './toggle-pad-labels-toolbar-item.js';
import { useToolbarFilter } from './use-toolbar-filter.js';

import type { KeyMapOptions } from 'react-hotkeys';
import type { IToolbarItem } from './toolbar-item.js';

export type CadRendererToolbarProps = {
	onOpenInNewWindow?: () => void;
};

export const CadRendererToolbar = memo(function CadRendererToolbar({ onOpenInNewWindow }: CadRendererToolbarProps) {
	const [selectedItem, setSelectedItem] = useState<IToolbarItem | undefined>();
	const [previousSelectedItem, setPreviousSelectedItem] = useState<IToolbarItem>();
	const [showAlternateItem, setShowAlternateItem] = useState(false);
	const [showNonExclusiveItems, setShowNonExclusiveItems] = useState<string[]>([]);
	const hoverInfoEnabled = useAtomValue(hoverInfoEnabledAtom);
	const { toolbarFilter, toolbarEvents } = useToolbarFilter();

	// Set the initial tool to the selection tool (very convoluted)
	const { drawingManager } = useDrawingManagerContext();
	const hasSetInitialTool = useRef(false);
	const selectionToolbarItemRef = useRef<SelectionToolbarItem | null>(null);
	useEffect(() => {
		if (!drawingManager || hasSetInitialTool.current) {
			return;
		}

		const intervalId = setInterval(() => {
			// yuck
			// Explanation here: https://dev.azure.com/cadstrom/Cadstrom/_git/monorepo/pullRequest/1093#1731961017
			if (hasSetInitialTool.current || !selectionToolbarItemRef.current) {
				return;
			}

			selectionToolbarItemRef.current.onActiveStateChanged(true);
			setSelectedItem(selectionToolbarItemRef.current);
			hasSetInitialTool.current = true;
			clearInterval(intervalId);
		}, 50);

		return () => {
			clearInterval(intervalId);
		};
	}, [drawingManager]);

	const toolbarShouldActivate = useCallback(
		(item: IToolbarItem) => {
			if (!item.isExclusive) {
				setShowNonExclusiveItems([...showNonExclusiveItems, item.id]);
			} else {
				if (selectedItem?.includeInHistory && selectedItem.id !== previousSelectedItem?.id) {
					setPreviousSelectedItem(selectedItem);
				}

				setSelectedItem(item);
			}

			toolbarEvents.toolSelected(item.id, item);
		},
		[previousSelectedItem?.id, selectedItem, showNonExclusiveItems, toolbarEvents],
	);

	const toolbarShouldDeactivate = useCallback(
		(item: IToolbarItem) => {
			if (!item.isExclusive) {
				setShowNonExclusiveItems(showNonExclusiveItems.filter((i) => i !== item.id));
			} else if (item.id === selectedItem?.id) {
				setSelectedItem(undefined);
			}
		},
		[selectedItem?.id, showNonExclusiveItems],
	);

	const selectedItemId = showAlternateItem && previousSelectedItem ? previousSelectedItem.id : selectedItem?.id;
	const isActive = useCallback(
		(itemId: string) => {
			// Read from the shared preference rather than this toolbar's own list, which cannot follow
			// a change made while the toggle is filtered out.
			if (itemId === ToggleHoverInfoToolbarItem.ItemId) {
				return hoverInfoEnabled;
			}

			if (itemId === selectedItemId) {
				return true;
			}

			if (showNonExclusiveItems.find((i) => i === itemId) !== undefined) {
				return true;
			}

			return false;
		},
		[hoverInfoEnabled, selectedItemId, showNonExclusiveItems],
	);

	// const { isProbeToolActive, setIsProbeToolActive } = useProbeToolContext();
	// if (selectedItemId === ProbeToolbarItem.ItemId && !isProbeToolActive) {
	// 	setIsProbeToolActive(true);
	// } else if (selectedItemId !== ProbeToolbarItem.ItemId && isProbeToolActive) {
	// 	setIsProbeToolActive(false);
	// }

	return (
		<div className='flex min-h-0 w-8 [scrollbar-width:none] flex-col items-center gap-3 overflow-auto border-r border-gray-a4 bg-gray-2 p-2 select-none'>
			<GlobalHotKeys
				keyMap={{
					use_alt: HOTKEY_USE_ALTERNATE,
					unuse_alt: HOTKEY_UNUSE_ALTERNATE,
				}}
				handlers={{
					use_alt: () => setShowAlternateItem(true),
					unuse_alt: () => setShowAlternateItem(false),
				}}
			/>

			{toolbarFilter(
				<SelectionToolbarItem
					key={SelectionToolbarItem.ItemId}
					ref={selectionToolbarItemRef}
					active={isActive(SelectionToolbarItem.ItemId)}
					onShouldActivate={toolbarShouldActivate}
					onShouldDeactivate={toolbarShouldDeactivate}
					toolbarEvents={toolbarEvents}
				/>,
			)}

			{/* {toolbarFilter(
					<ProbeToolbarItem
						key={ProbeToolbarItem.ItemId}
						active={isActive(ProbeToolbarItem.ItemId)}
						onShouldActivate={toolbarShouldActivate}
						onShouldDeactivate={toolbarShouldDeactivate}
						toolbarEvents={toolbarEvents}
					/>,
				)}

				{toolbarFilter(
					<MeasureToolbarItem
						key={MeasureToolbarItem.ItemId}
						buttonProps={{ disabled: activeDesign.modelType !== 'pcb' }}
						active={isActive(MeasureToolbarItem.ItemId)}
						onShouldActivate={toolbarShouldActivate}
						onShouldDeactivate={toolbarShouldDeactivate}
						toolbarEvents={toolbarEvents}
					/>,
				)}

				<div className='w-full shrink-0 basis-px bg-gray-a4' /> */}

			{toolbarFilter(
				<LayersToolbarItem
					key={LayersToolbarItem.ItemId}
					active={isActive(LayersToolbarItem.ItemId)}
					onShouldActivate={toolbarShouldActivate}
					onShouldDeactivate={toolbarShouldDeactivate}
					toolbarEvents={toolbarEvents}
				/>,
			)}

			{/* {toolbarFilter(
					<TogglePolygonFillToolbarItem
						key={TogglePolygonFillToolbarItem.ItemId}
						active={isActive(TogglePolygonFillToolbarItem.ItemId)}
						onShouldActivate={toolbarShouldActivate}
						onShouldDeactivate={toolbarShouldDeactivate}
						toolbarEvents={toolbarEvents}
					/>,
				)} */}

			{toolbarFilter(
				<TogglePadLabelsToolbarItem
					key={TogglePadLabelsToolbarItem.ItemId}
					active={isActive(TogglePadLabelsToolbarItem.ItemId)}
					onShouldActivate={toolbarShouldActivate}
					onShouldDeactivate={toolbarShouldDeactivate}
					toolbarEvents={toolbarEvents}
				/>,
			)}

			{toolbarFilter(
				<ToggleHoverInfoToolbarItem
					key={ToggleHoverInfoToolbarItem.ItemId}
					active={isActive(ToggleHoverInfoToolbarItem.ItemId)}
					onShouldActivate={toolbarShouldActivate}
					onShouldDeactivate={toolbarShouldDeactivate}
					toolbarEvents={toolbarEvents}
				/>,
			)}

			<div className='w-full shrink-0 basis-px bg-gray-a4' />

			<ClearSelectionToolbarItem
				key={ClearSelectionToolbarItem.ItemId}
				active={isActive(ClearSelectionToolbarItem.ItemId)}
				onShouldActivate={toolbarShouldActivate}
				onShouldDeactivate={toolbarShouldDeactivate}
				toolbarEvents={toolbarEvents}
			/>

			{onOpenInNewWindow && (
				<>
					<div className='mt-auto w-full shrink-0 basis-px bg-gray-a4' />
					<Tooltip arrow title={<p className='text-sm'>Open design canvas in new window</p>} placement='right'>
						<IconButton
							size='small'
							aria-label='Open design canvas in new window'
							onClick={onOpenInNewWindow}
							className='-m-1 p-1'
						>
							<OpenInNewIcon fontSize='small' />
						</IconButton>
					</Tooltip>
				</>
			)}
		</div>
	);
});

const HOTKEY_USE_ALTERNATE: KeyMapOptions = {
	sequence: 'alt',
	action: 'keydown',
};
const HOTKEY_UNUSE_ALTERNATE: KeyMapOptions = {
	sequence: 'alt',
	action: 'keyup',
};
